import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    Context,
    ENTITY_IA_CONTENT_MODAL_LABELS,
    GENERATE_IA_CONTENT_PAYLOAD,
    Length,
    Tone,
    TYPE_PROMPT,
    UserPrompt,
} from '@app/_models/entity-ia-content/entity-ia-content.model';
import { Select, Store } from '@ngxs/store';
import { ClearIaContent, GenerateIaContent } from './store/entity-ia-content.actions';
import { TranslateService } from '@ngx-translate/core';
import { EntityIaContentState } from './store/entity-ia-content.store';
import { Observable } from 'rxjs';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-entity-ia-content-modal',
    templateUrl: './entity-ia-content-modal.component.html',
    styleUrls: ['./entity-ia-content-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityIaContentModalComponent implements OnDestroy {
    contentForm: FormGroup;

    showError: {
        name: boolean;
        description: boolean;
    };

    createLoading: boolean = false;

    @Select(EntityIaContentState.getLoading)
    createLoading$: Observable<boolean>;

    @Select(EntityIaContentState.getGeneratedContent)
    generatedContent$: Observable<string>;

    generatedContent: string;
    isContentGenerated: boolean;

    readonly nameMax: number = 100;
    readonly descriptionMax: number = 300;

    tonos: { id: Tone; name: string }[] = [
        { id: 'formal', name: `${this.translate.instant('app.global.modals.EntityIaContent.options.tonos.formal')}` },
        {
            id: 'amigable',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.tonos.amigable')}`,
        },
        { id: 'tecnico', name: `${this.translate.instant('app.global.modals.EntityIaContent.options.tonos.tecnico')}` },
        {
            id: 'vendedor',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.tonos.vendedor')}`,
        },
        {
            id: 'creativo',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.tonos.creativo')}`,
        },
        {
            id: 'informativo',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.tonos.informativo')}`,
        },
        {
            id: 'persuasivo',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.tonos.persuasivo')}`,
        },
        {
            id: 'entretenido',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.tonos.entretenido')}`,
        },
        {
            id: 'analitico',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.tonos.analitico')}`,
        },
        {
            id: 'personal',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.tonos.personal')}`,
        },
    ];

    longitudes: { id: Length; name: string }[] = [
        {
            id: 'corto',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.longitudes.corto')}`,
        },
        {
            id: 'medio',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.longitudes.medio')}`,
        },
        {
            id: 'largo',
            name: `${this.translate.instant('app.global.modals.EntityIaContent.options.longitudes.largo')}`,
        },
    ];

    constructor(
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<EntityIaContentModalComponent>,
        @Inject(MAT_DIALOG_DATA) public params: ENTITY_IA_CONTENT_MODAL_LABELS,
        private store: Store,
        private translate: TranslateService
    ) {
        this.showError = {
            name: false,
            description: false,
        };

        this.buildForm();

        this.generatedContent$.subscribe((generatedContent: string) => {
            if (generatedContent?.length) {
                this.generatedContent = generatedContent;
                this.contentForm.get('promptUsuario').setValue(generatedContent);
                this.isContentGenerated = true;
            }
        });
    }
    ngOnDestroy(): void {
        this.store.dispatch(new ClearIaContent());
    }

    private buildForm(): void {
        this.contentForm = this.fb.group({
            name: [
                this.params?.entityTitle ?? '',
                this.params?.useTitle
                    ? [Validators.required, Validators.maxLength(this.nameMax)]
                    : [Validators.maxLength(this.nameMax)],
            ],
            promptUsuario: ['', [Validators.required, Validators.maxLength(this.descriptionMax)]],
            tono: [this.tonos[0].id, [Validators.required]],
            longitud: [this.longitudes[0].id, [Validators.required]],
        });

        this.contentForm.get('promptUsuario').valueChanges.subscribe((content: string) => {
            if (!content?.length) {
                this.isContentGenerated = false;
                this.generatedContent = null;
            }
        });
    }

    public focusOut(e, key: string) {
        if (e) {
            this.showError[key] = true;
        }
    }

    generateContent(): void {
        const payload = this.generarPayloadDesdeFormulario();
        this.store.dispatch(new GenerateIaContent(payload)).subscribe({
            error: (err) => {
                this.closeDialog(null);
            },
        });
    }

    private generarPayloadDesdeFormulario(): GENERATE_IA_CONTENT_PAYLOAD {
        const formData = this.contentForm.value;
        const tono = formData['tono'] as Tone;
        const longitud = formData['longitud'] as Length;
        const promptUsuario = formData['promptUsuario'] as string;
        const valorContexto = formData['name'];

        const userPrompt: UserPrompt<
            Tone,
            Length,
            string
        > = `Tono: ${tono}, Longitud del texto: ${longitud}, Prompt: ${promptUsuario}`;

        let context: Context;

        switch (this.params?.typePrompt) {
            case TYPE_PROMPT.PRODUCT_CONTENT_GENERATION:
                context = `Nombre de producto: ${valorContexto}`;
                break;
            case TYPE_PROMPT.POST_CONTENT_GENERATION:
                context = `Nombre de post: ${valorContexto}`;
                break;
            default:
                context = `Nombre de producto: ${valorContexto}`;
                break;
        }

        return {
            typePrompt: this.params?.typePrompt,
            userPrompt,
            context,
        };
    }

    setContent(): void {
        this.closeDialog(this.generatedContent);
    }

    closeDialog(generatedText?: string): void {
        this.dialogRef.close(generatedText);
    }

    closeDropdown(dropdown: NgbDropdown): void {
        dropdown.close();
    }
}
